// tslint:disable:max-line-length

import { CommonModule } from '@angular/common';
import { NgModule, Injector } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { createCustomElement } from '@angular/elements';
import { RouterModule } from '@angular/router';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { FormActionsComponent } from '../form/actions/actions.component';
import { DocumentChecklistComponent } from '../form/document-checklist/document-checklist.component';
import { ApplicationEzComponent } from '../form/fees/application-ez/application-ez.component';
import { ApplicationIteComponent } from '../form/fees/application-ite/application-ite.component';
import { ApplicationQjComponent } from '../form/fees/application-qj/application-qj.component';
import { ApplicationRtaComponent } from '../form/fees/application-rta/application-rta.component';
import { ApplicationRdComponent } from '../form/fees/application-rd/application-rd.component';
import { FormFeesComponent } from '../form/fees/fees.component';
import { FormFlatFeesComponent } from '../form/fees/flat/flat.component';
import { FormComponent } from '../form/form.component';
import { FormPaymentsComponent } from '../form/payments/payments.component';
import { FormSignatureComponent } from '../form/signature/signature.component';
import { ProjectModule } from '../project/project.module';
import { SharedModule } from '../shared/shared.module';
import { UserModule } from '../user/user.module';
import { FormConfirmationResolver } from './resolvers/confirmation.resolver';
import { FormPayeeResolver } from './resolvers/payee.resolver';
import { FormResolver } from './resolvers/form.resolver';
import { FormShareService } from './services/share.service';
import { FormActionHandlerAuthorityService } from './services/action-handler-factory.service';
import { UnlockFormActionHandlerService } from './services/action-handlers/unlock.service';
import { CompleteReviewFormActionHandlerService } from './services/action-handlers/complete-review.service';
import { SubmitFormActionHandlerService } from './services/action-handlers/submit.service';
import { SubmitCostReportActionHandlerService } from './services/action-handlers/submit-cost-report.service';
import { SignFormActionHandlerService } from './services/action-handlers/sign.service';
import { SaveFormActionHandlerService } from './services/action-handlers/save.service';
import { RequestSignatureFormActionHandlerService } from './services/action-handlers/request-signature.service';
import { RequestPaymentFormActionHandlerService } from './services/action-handlers/request-payment.service';
import { PrintFormActionHandlerService } from './services/action-handlers/print.service';
import { PayFormActionHandlerService } from './services/action-handlers/pay.service';
import { NoSignFormActionHandlerService } from './services/action-handlers/no-sign.service';
import { CloseFormActionHandlerService } from './services/action-handlers/close.service';
import { FormDataService } from './services/data.service';
import { FormValidationService } from './services/validation.service';
import { FormEmailService } from './services/email.service';
import { FormStatusService } from './services/status.service';
import { FormPermissionService } from './services/permission.service';
import { FormConfirmationService } from './services/confirmation.service';
import { FormTitleService } from './services/title.service';
import { FormFeeService } from './services/fee.service';
import { ApplicationFilmComponent } from './fees/application-film/application-film.component';
import { SaveCreditsFormActionHandlerService } from './services/action-handlers/save-credits.service';
import { ProgramTypeCheckboxComponent } from './program-type-checkbox/program-type-checkbox.component';
import { CompleteAuditActionHandlerService } from './services/action-handlers/complete-audit.service';
import { CreateInitialCertLetterService } from './services/action-handlers/create-initial-cert-letter.service';
import { CreateFinalCertAttestationService } from './services/action-handlers/create-final-cert-attestation.service';
import { CreateFinalCertLetterService } from './services/action-handlers/create-final-cert-letter.service';
import { SignaturesSendService } from '../management/signatures/send/send.service';
import { ApplicationDmComponent } from './fees/application-dm/application-dm.component';
import { ResendInitialCertLetterService } from './services/action-handlers/resend-initial-cert-letter.service';
import { SignaturesManageService } from '../management/signatures/manage/manage.service';
import { AddENTIdActionHandlerService } from './services/action-handlers/add-ent-id.service';
import { SetReceivedActionHandlerService } from './services/action-handlers/set-received.service';
import { ManagerActionsComponent } from './manager-actions/manager-actions.component';
import { CheckOutActionHandlerService } from './services/action-handlers/check-out.service';
import { CheckOutComponent } from './check-out/check-out.component';
import { CertificateComplianceChecklistComponent } from '../incentive/compliance/coc/coc-checklist/coc-checklist.component';
import { StepChecklistComponent } from '../step/application/step-checklist/step-checklist.component';
import {StepModule} from '../step/step.module';
import { ApplicationSupplementalQuestionsIteComponent } from '../incentive/components/application-supplemental-questions-ite/application-supplemental-questions-ite.component';
import {PrrModule} from '../prr/prr.module';
import {SendIrrevocableDesigneeAgreementActionHandlerService} from './services/action-handlers/send-irrevocable-designee-agreement.service';
import { SendDeskAuditActionHandlerService } from './services/action-handlers/send-desk-audit.service';
import {
  ManagerReviewCompleteActionHandlerService,
  AssistantDirectorReviewCompleteActionHandlerService,
  DirectorReviewCompleteActionHandlerService
} from './services/action-handlers/rd-review-stage.service';
import { RdReturnToManagerActionHandlerService } from './services/action-handlers/rd-return-to-manager.service';
import { SendCertificationActionHandlerService } from './services/action-handlers/send-certification.service';

/**
 * The IncentiveModule is technically a submodule of project -- this module is intended to be used as
 * a pool of both simple and complex components that will be used for all incentive program forms
 *
 * All components here must implement a readonly mode where inputs/dropdowns cannot be modified
 */
@NgModule({
    imports: [
        CommonModule,
        CurrencyMaskModule,
        FormsModule,
        ProjectModule,
        ReactiveFormsModule,
        RouterModule,
        SharedModule,
        UserModule,
        StepModule,
        PrrModule,
    ],
  declarations: [
    FormComponent,
    FormActionsComponent,
    FormSignatureComponent,
    FormPaymentsComponent,
    FormFlatFeesComponent,
    FormFeesComponent,
    ApplicationQjComponent,
    ApplicationEzComponent,
    ApplicationRtaComponent,
    ApplicationIteComponent,
    ApplicationRdComponent,
    DocumentChecklistComponent,
    ApplicationFilmComponent,
    ProgramTypeCheckboxComponent,
    ApplicationDmComponent,
    ManagerActionsComponent,
    CheckOutComponent,
    CertificateComplianceChecklistComponent,
    StepChecklistComponent,
    ApplicationSupplementalQuestionsIteComponent
  ],
  exports: [
    FormComponent,
    FormActionsComponent,
    ManagerActionsComponent,
    FormFeesComponent,
    CheckOutComponent
  ],
  providers: [
    AddENTIdActionHandlerService,
    CreateInitialCertLetterService,
    ResendInitialCertLetterService,
    CreateFinalCertAttestationService,
    CreateFinalCertLetterService,
    FormTitleService,
    FormResolver,
    FormConfirmationResolver,
    FormPayeeResolver,
    FormActionHandlerAuthorityService,
    FormConfirmationService,
    FormDataService,
    FormEmailService,
    FormPermissionService,
    FormStatusService,
    FormValidationService,
    CloseFormActionHandlerService,
    NoSignFormActionHandlerService,
    PayFormActionHandlerService,
    PrintFormActionHandlerService,
    SetReceivedActionHandlerService,
    SendIrrevocableDesigneeAgreementActionHandlerService,
    RequestPaymentFormActionHandlerService,
    RequestSignatureFormActionHandlerService,
    SaveCreditsFormActionHandlerService,
    SaveFormActionHandlerService,
    SubmitCostReportActionHandlerService,
    SignaturesSendService,
    SignaturesManageService,
    SignFormActionHandlerService,
    SubmitFormActionHandlerService,
    CompleteReviewFormActionHandlerService,
    UnlockFormActionHandlerService,
    CompleteAuditActionHandlerService,
    SendDeskAuditActionHandlerService,
    ManagerReviewCompleteActionHandlerService,
    AssistantDirectorReviewCompleteActionHandlerService,
    DirectorReviewCompleteActionHandlerService,
    RdReturnToManagerActionHandlerService,
    SendCertificationActionHandlerService,
    FormShareService,
    FormFeeService
  ],
  entryComponents: []
})
export class FormModule {
  constructor(injector: Injector) {}
}
